import type { Person, Role } from '@/domain/types';
import { rt } from './runtime';
import { DomainError, inTx, newId, newMeta, touch, writeAudit } from './write';

/** The history log never holds a PIN hash, only whether one is set. */
const forLog = (p: Person | undefined | null) => (p ? { ...p, pinHash: p.pinHash ? '(set)' : null } : null);

async function save(next: Person, before: Person | undefined | null, action: string, summary: string) {
  await rt().db.people.put(next);
  await writeAudit({ action, entity: 'people', entityId: next.id, summary, before: forLog(before), after: forLog(next) });
}

export function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .join('')
    .slice(0, 3);
}

/** PINs are kept as a hash with the person's id mixed in, never as typed. */
export async function hashPin(personId: string, pin: string): Promise<string> {
  const bytes = new TextEncoder().encode(`lastcall:${personId}:${pin}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function checkPin(person: Person, pin: string): Promise<boolean> {
  if (!person.pinHash) return true;
  return (await hashPin(person.id, pin)) === person.pinHash;
}

function validate(p: { name: string; initials: string }) {
  if (!p.name.trim()) throw new DomainError('Type a name.');
  if (p.name.trim().length > 40) throw new DomainError('That name is too long (40 letters at most).');
  if (!p.initials.trim()) throw new DomainError('Type initials (they go in the “Count Initial” row).');
  if (p.initials.trim().length > 4) throw new DomainError('Initials are 4 letters at most.');
}

function assertMayChange(target: { id?: string; role: Role }, newRole: Role) {
  const me = rt().actor;
  if (me.role === 'owner') return;
  if (me.role === 'manager' && target.role === 'staff' && newRole === 'staff') return;
  if (target.id === me.userId && newRole === target.role) return;
  throw new DomainError(newRole !== 'staff' ? 'Only an owner can make managers and owners.' : 'Only an owner can change managers.');
}

export async function addPerson(input: { name: string; initials?: string; role: Role; pin?: string | null }): Promise<Person> {
  assertMayChange({ role: 'staff' }, input.role);
  const initials = (input.initials ?? '').trim() || initialsOf(input.name);
  validate({ name: input.name, initials });
  if (input.pin != null && input.pin !== '' && !/^\d{4}$/.test(input.pin)) throw new DomainError('A PIN is 4 digits.');
  const meta = newMeta(newId('usr-'));
  const person: Person = { ...meta, name: input.name.trim(), initials, role: input.role, active: true, pinHash: input.pin ? await hashPin(meta.id, input.pin) : null };
  await inTx(['people'], () => save(person, null, 'person.add', `Added ${person.name} (${person.role})`));
  return person;
}

export async function updatePerson(id: string, patch: { name?: string; initials?: string; role?: Role; active?: boolean }): Promise<Person> {
  return inTx(['people'], async () => {
    const before = await rt().db.people.get(id);
    if (!before) throw new DomainError('That person no longer exists.');
    assertMayChange(before, patch.role ?? before.role);
    const next = touch(before, { ...patch, ...(patch.name != null ? { name: patch.name.trim() } : {}), ...(patch.initials != null ? { initials: patch.initials.trim() } : {}) });
    validate(next);
    const owners = (await rt().db.people.toArray()).filter((p) => p.active && p.role === 'owner' && p.id !== id);
    if (before.role === 'owner' && (next.role !== 'owner' || !next.active) && owners.length === 0) throw new DomainError('Keep at least one active owner, or nobody can change the settings.');
    await save(next, before, 'person.update', `Changed ${next.name}`);
    return next;
  });
}

/** Sets or clears (pin = null) a 4-digit PIN. */
export async function setPin(id: string, pin: string | null): Promise<void> {
  if (rt().actor.userId !== id && rt().actor.role !== 'owner') throw new DomainError('Only an owner can set someone else’s PIN.');
  if (pin != null && !/^\d{4}$/.test(pin)) throw new DomainError('A PIN is 4 digits.');
  const pinHash = pin == null ? null : await hashPin(id, pin);
  await inTx(['people'], async () => {
    const before = await rt().db.people.get(id);
    if (!before) throw new DomainError('That person no longer exists.');
    const next = touch(before, { pinHash });
    await save(next, before, pin ? 'person.pin_set' : 'person.pin_clear', pin ? `Set a PIN for ${next.name}` : `Removed the PIN for ${next.name}`);
  });
}
